import { useContext, useRef, useState } from 'react'
import { useModal } from 'react-modal-hook'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { Form, FormHandles, Select, TextField } from '@minily/components'
import { useOrulAuth } from '@rhaimes/react'

import { Button, Container, Footer, Icon } from 'core/components'
import { formatToPhone, isValidURL } from 'core/helpers'
import { sanitizeField } from 'core/validator'

import { AuthContext } from 'providers/auth'

import { Actions, TRootReducer } from 'store'

import PhoneChangeDrawer from './components/PhoneChangeDrawer'
import {
  Avatar,
  AvatarPreview,
  Cover,
  FormContainer,
  Username,
  Wrapper,
} from './styles'

type UserProfileProps = {
  /**
   * Quando informado, mostra um botão de voltar no lugar da navegação.
   *
   * Sem organização selecionada a tab bar não é renderizada e o "Alterar Empresa" abaixo só
   * aparece com mais de uma empresa — quem tem zero ficaria preso na tela sem saída.
   */
  onBack?: () => void
}

function UserProfile({ onBack }: UserProfileProps) {
  const { attributes: userAttributes } = useSelector(
    (state: TRootReducer) => state.user,
  )
  const { orgs, currentOrg } = useSelector((state: TRootReducer) => state.app)
  const { signOut } = useContext(AuthContext)
  const { updateProfile } = useOrulAuth()
  const formRef = useRef<FormHandles>(null)
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()

  const [showPhoneChange, hidePhoneChange] = useModal(
    () => (
      <PhoneChangeDrawer
        currentPhone={formatToPhone(
          userAttributes.phone_number?.slice(3) ?? '',
        )}
        onHide={hidePhoneChange}
        onSuccess={() => {
          hidePhoneChange()
          // O refreshSession do hook ja trouxe o telefone novo para a sessao;
          // recarregar garante que o formulario e o restante da tela leiam o
          // valor atualizado sem espalhar sincronizacao manual de estado.
          window.location.reload()
        }}
      />
    ),
    [userAttributes.phone_number],
  )

  // Normaliza gender: Cognito retorna "male"|"female"|"unknown"; Select espera 0|1|2|9
  const validGenders = [0, 1, 2, 9]
  const genderFromString: Record<string, number> = {
    male: 1,
    female: 2,
    unknown: 9,
  }
  const rawGender = userAttributes.gender
  const normalizedGender =
    typeof rawGender === 'string'
      ? genderFromString[rawGender] ?? 0
      : typeof rawGender === 'number' && validGenders.includes(rawGender)
        ? rawGender
        : 0

  const onSubmit = async (
    data: Pick<typeof userAttributes, 'name' | 'gender' | 'birthdate'>,
  ) => {
    try {
      setLoading(true)

      const profileData = {
        name: data.name,
        gender: data.gender,
        birthdate: data.birthdate
          ?.split('/')
          .reverse()
          .join('-')
          .replace(/\s/g, ''),
      }

      if (profileData.gender && ![0, 1, 2, 9].includes(+profileData.gender)) {
        toast.error('Gênero inválido')
        return
      }
      if (profileData.name && profileData.name.length > 100) {
        toast.error('Nome deve ter no máximo 100 caracteres')
        return
      }

      const genderMap: Record<number, 'male' | 'female' | 'unknown'> = {
        1: 'male',
        2: 'female',
        9: 'unknown',
      }
      const genderValue = profileData.gender ? +profileData.gender : 0
      const updateData = {
        name: profileData.name,
        birthdate: profileData.birthdate,
        ...(genderValue !== 0 && genderMap[genderValue]
          ? { gender: genderMap[genderValue] }
          : {}),
      }

      const result = await updateProfile(updateData)

      dispatch(
        Actions.user.setData({
          attributes: { ...userAttributes, ...profileData },
        }),
      )

      if (
        result.requiresVerification?.email ||
        result.requiresVerification?.phone
      ) {
        toast.info(
          'Perfil atualizado! Verifique seu email/telefone para confirmar as alterações.',
        )
      } else {
        toast.success('Perfil atualizado com sucesso!')
      }
    } catch (error: any) {
      const errorMessage =
        error.message ||
        'Não foi possível atualizar os dados. Tente novamente.'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Wrapper>
      <Cover>
        <AvatarPreview>
          <Avatar>
            {isValidURL(userAttributes.picture) ? (
              <img src={userAttributes.picture} alt="" />
            ) : (
              userAttributes.name?.substring(0, 1) || (
                <Icon icon="user" size="35px" />
              )
            )}
          </Avatar>
        </AvatarPreview>
        <Username>{userAttributes.name}</Username>
      </Cover>
      <FormContainer>
        <Container>
          <Form
            onSubmit={onSubmit}
            ref={formRef}
            initialData={{
              name: userAttributes.name,
              phone: userAttributes.phone_number?.slice(3) ?? '',
              email: userAttributes.email,
              birthdate: sanitizeField({
                type: 'date',
                value: userAttributes.birthdate,
              }),
              gender: normalizedGender,
            }}
          >
            <TextField label="Nome" name="name" type="text" />
            <TextField
              label="Telefone"
              name="phone"
              type="text"
              mask="(00) 0 0000-0000"
              style={{ opacity: 0.4 }}
              tooltipe
              disabled
            />
            <button
              type="button"
              onClick={showPhoneChange}
              style={{
                display: 'block',
                marginTop: -8,
                marginBottom: 8,
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                color: '#2f80ed',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Alterar telefone
            </button>
            <TextField
              label="Email"
              name="email"
              type="email"
              style={{ opacity: 0.4 }}
              disabled
            />
            <span
              style={{
                display: 'block',
                paddingBottom: 15,
                textAlign: 'right',
                fontSize: 12,
                opacity: 0.4,
              }}
            >
              Não é possível alterar o e-mail.
            </span>

            <TextField
              type="text"
              mask="00 / 00 / 0000"
              label="Data de Nascimento"
              name="birthdate"
            />

            <Select
              label="Gênero"
              name="gender"
              options={[
                {
                  label: 'Selecionar',
                  value: 0,
                },
                {
                  label: 'Masculino',
                  value: 1,
                },
                {
                  label: 'Feminino',
                  value: 2,
                },
                {
                  label: 'Prefiro não dizer',
                  value: 9,
                },
              ]}
            />
            <br />
            <Button
              color="success"
              type="submit"
              loading={loading}
              disabled={loading}
              style={{ width: '100%' }}
            >
              Salvar
            </Button>

            <hr style={{ margin: '50px 0', opacity: 0.2 }} />

            {orgs.length > 1 && (
              <Button
                type="button"
                color="info"
                onClick={() => {
                  window.location.href = '/'
                }}
              >
                <span>Alterar Empresa</span>
              </Button>
            )}
            {!!onBack && (
              <Button type="button" color="info" onClick={onBack}>
                <span>Voltar</span>
              </Button>
            )}
            <br />
            <Button
              type="button"
              color="primary"
              onClick={() => {
                signOut()
              }}
            >
              <span>Encerrar Sessão</span>
            </Button>
            <br />
            {/* Os termos são por empresa: sem organização a URL viraria `/undefined`. */}
            {!!currentOrg && (
              <div style={{ paddingBottom: '80px 0', textAlign: 'center' }}>
                <a
                  href={`https://terms.toty.app/${currentOrg.settings.key}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 13, color: '#000000' }}
                >
                  Termos de Uso & Política de Privacidade
                </a>
              </div>
            )}
          </Form>
        </Container>
        <Footer />
      </FormContainer>
    </Wrapper>
  )
}

export { UserProfile }
